/**
 * Sales Insights account page (/pc/insights/accounts/:accountId): one account
 * in the rep's book. Fetches the account once and hands each section the part
 * it shows. Sections we can't fill yet keep their space with a Gap note.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "wouter";
import { Layout } from "@/components/layout";
import { useAuthenticatedQuery } from "@/hooks/use-api";
import { useSalesInsightsStatus } from "@/hooks/use-sales-insights";
import { PC_INSIGHTS_QUERY_KEY, pcInsightsAccountQueryKey } from "@/requests/pc-insights";
import type {
  SalesInsightsAccountResponse,
  SalesInsightsBookResponse,
} from "@onsemble/shared/types/sales-insights";
import { InsightsTheme } from "../InsightsTheme";
import { MISSING } from "../format";
import { BrandHeader } from "../shared/BrandHeader";
import type { Period } from "../shared/PeriodTabs";
import { Header } from "./sections/Header";
import { Hero } from "./sections/Hero";
import { KeyFacts } from "./sections/KeyFacts";
import { EmptySection } from "./sections/EmptySection";
import { Notes } from "./sections/Notes";

export default function InsightsAccountDetail() {
  const { t } = useTranslation();
  // The account's Eclipse key from the URL, e.g. "902|145088"
  const { accountId = "" } = useParams<{ accountId: string }>();
  // A marker for another PC: say so instead of asking the server for a 403
  const status = useSalesInsightsStatus();
  // Month to date or year to date, from the MTD | YTD picker; starts on month
  const [period, setPeriod] = useState<Period>("month");

  const {
    data: account,
    isLoading,
    error,
  } = useAuthenticatedQuery<SalesInsightsAccountResponse>(
    [...pcInsightsAccountQueryKey(accountId)],
    { enabled: status.kind === "ok" && !!accountId },
  );
  // The rep's book, only for their name in the header; usually already
  // cached from the dashboard, and the page doesn't wait on it
  const { data: book } = useAuthenticatedQuery<SalesInsightsBookResponse>(
    [...PC_INSIGHTS_QUERY_KEY],
    { enabled: status.kind === "ok" },
  );

  return (
    <Layout
      headerContent={<span className="font-semibold">{t("pages.pcInsights.title")}</span>}
    >
      {status.kind === "wrong-pc" ? (
        // Marker is for another PC, or this org has no PC id
        <p className="text-muted-foreground py-6 text-center">
          {t("pages.pcInsights.notFoundInOrg")}
        </p>
      ) : isLoading ? (
        <div className="h-10 bg-muted rounded animate-pulse" />
      ) : error || !account ? (
        // Not in this rep's book (the server says 404), or the request failed
        <p className="text-muted-foreground py-6 text-center">
          {t("pages.pcInsights.account.notFound")}
        </p>
      ) : (
        <InsightsTheme>
          <BrandHeader
            asOf={account.asOf}
            repName={book && (book.rep.osrName ?? book.rep.osrId)}
            profitCenters={book?.profitCenters}
          />
          <Header account={account} />
          <Hero account={account} period={period} onPeriodChange={setPeriod} />
          <KeyFacts account={account} />
          {/* No data yet: each keeps its place with a dash or a short note */}
          <EmptySection
            title={t("pages.pcInsights.account.placeholders.buyLinesTitle")}
            note={MISSING}
          />
          <EmptySection
            title={t("pages.pcInsights.account.placeholders.searchesTitle")}
            note={t("pages.pcInsights.account.placeholders.searchesNotOnPlatform")}
          />
          <EmptySection
            title={t("pages.pcInsights.account.placeholders.itemsTitle")}
            note={MISSING}
          />
          {/* Add and read notes on this account */}
          <Notes accountId={account.accountId} />
        </InsightsTheme>
      )}
    </Layout>
  );
}
